import React, { useContext, useMemo, useState } from 'react';
import EDDialog from '../../../../../../../BaseComponents/Dialog/EDDialog';
import { ObjektVyKort } from '../../../../../_Common/Components/BusinessCollectionComponents/ObjektVyKort/ObjektVyKort';
import EDBox from '../../../../../../../BaseComponents/EDBox/EDBox';
import {
  dateTimeStringIsValidDate,
  diffDay2,
  incDay,
} from '../../../../../../../Utils/dateUtils';
import EDTypography from '../../../../../../../BaseComponents/Typography/EDTypography';
import { IFormData } from '../../../../../_Common/Components/BusinessCollectionComponents/BusinessDataForm/Utils/BusinessDataFormUtils';
import CopyActivityDoubleBooking from './CopyActivityDoubleBooking/CopyActivityDoubleBooking';
import { overlappedBookings } from '../../Utils/AktivitetUtils';
import { QueryHookOptions, useQuery } from '@apollo/client';
import {
  GetResursDoubleBookingQuery,
  GetResursDoubleBookingQueryVariables,
} from '../../Queries/_QueryTypes/GetResursDoubleBookingQuery';
import { RutinSettingsContext } from '../../../../../_Common/Components/RutinAvailabilityContext/RutinAvailabilityContext';
import { ActiveDatabaseContext } from '../../../../../../../Components/ActiveDatabaseContext/ActiveDatabaseContext';
import resursDoubleBookingQuery from '../../Queries/GetResursDoubleBookingQuery.gql';
import { combineDateAndTime } from '../../Utils/AktivitetDateTimeFormatter';
import {
  ICurrentDayBooking,
  IOverlappedBooking,
} from '../../AktivitetResursValjare';
import { clone } from 'ramda';
import {
  BOGetXBridgeBookingsQuery,
  BOGetXBridgeBookingsQueryVariables,
} from '../../../../Rutiner/Kalender/Queries/XbridgeQuery/_QueryTypes/BOGetXBridgeBookingsQuery';
import xbridgeQuery from '../../../../Rutiner/Kalender/Queries/XbridgeQuery/BOGetXBridgeBookings.gql';
import { useMutation } from '@apollo/client';
import kopieraAktivitetMutation from '../../Mutations/KopieraAktivitetMutation.gql';
import {
  KopieraAktivitetMutation,
  KopieraAktivitetMutationVariables,
} from '../../Mutations/_QueryTypes/KopieraAktivitetMutation';
import EDAvenyProgress from '../../../../../../../BaseComponents/ProgressIndicators/AvenyProgress/EDAvenyProgress';
import { themeColors } from '../../../../../../../BaseComponents/ThemeProvider/AvenyThemeColors';
import CopyActivityDatePicker from './CopyActivityDatePicker/CopyActivityDatePicker';

interface IProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  formContext: IFormData;
  resursLista: string[];
  onActivityCopied: (newActivityId: string) => void;
}

const isInvalidDate = (startDateNew: string): boolean => {
  if (!startDateNew) {
    return true;
  }
  const removeHyphensFromDate = startDateNew.replaceAll('-', '');
  return !dateTimeStringIsValidDate(removeHyphensFromDate);
};

const isDateBehindCurrent = (dateString: string): boolean => {
  if (!dateString) {
    return false;
  }
  const today = new Date().toISOString().split('T')[0]; // Gets "yyyy-mm-dd" format
  return dateString < today;
};

const getNewEndDate = (
  startDateNew: string,
  oldStartDate: string,
  oldEndDate: string
): string => {
  const newStartDate = new Date(startDateNew);
  const currentStartDate = new Date(oldStartDate);
  const currentEndDate = new Date(oldEndDate);

  if (
    isInvalidDate(startDateNew) ||
    !startDateNew ||
    !oldStartDate ||
    !oldEndDate
  ) {
    return '';
  }

  const diffInDays = diffDay2(currentStartDate, currentEndDate);
  const newEnd = incDay(newStartDate, diffInDays);

  if (isNaN(newEnd.getTime())) {
    return '';
  }

  return newEnd.toISOString().split('T')[0];
};

const mapXbridgeBookings = (
  xbridgeData: BOGetXBridgeBookingsQuery | undefined
): ICurrentDayBooking[] => {
  return (
    xbridgeData?.rtn?.BOGetXBridgeBookings?.map(item => ({
      aktId: 'Outlook',
      BO_RESURS_ID: item.resursId ?? '',
      resStart: item.startdatum ?? '',
      resEnd: item.slutdatum ?? '',
      bokNr: '',
      aktTitel: item.titel ?? '',
    })) ?? []
  );
};

const mapCurrentBookingToOverlappedBookings = (
  formContext: IFormData,
  currentStartDate: string,
  currentEndDate: string
): IOverlappedBooking[] => {
  return (
    formContext.formState.currentValues._Associations.AktBokRubrik?.map(
      (item: any) => ({
        rubrikId: item?.id ?? '',
        resourceName: item?._Associations?.Resurs?.BO_RESURS_NAMN ?? '',
        bastyp:
          item?._Associations?.Resurs?._Associations?.ResRestyp?.Bastyp ?? '',
        resStart: combineDateAndTime(
          currentStartDate,
          formContext.formState.currentValues.FRANTID
        ),
        resEnd: combineDateAndTime(
          currentEndDate,
          formContext.formState.currentValues.TILLTID
        ),
        bokNr: formContext.formState.currentValues.BOKNINGSNUMMER ?? '',
        aktTitel: formContext.formState.currentValues.TITEL ?? '',
      })
    ) ?? []
  );
};

const CopyActivityDialog: React.FC<React.PropsWithChildren<IProps>> = props => {
  const { open, setOpen, formContext, resursLista } = props;
  const routineAvailabilityContext = useContext(RutinSettingsContext);
  const routineID = routineAvailabilityContext.activeRoutine?.routine.rutinID;
  const { activeDatabase } = useContext(ActiveDatabaseContext);
  const currentEndDate = formContext?.formState?.currentValues?.TILLDATUM || '';
  const currentStartDate =
    formContext?.formState?.currentValues?.FRANDATUM || '';
  const [startDate, setStartDate] = useState<string>('');

  const endDate = useMemo(() => {
    if (startDate && currentStartDate && currentEndDate) {
      return getNewEndDate(startDate, currentStartDate, currentEndDate);
    }
    return '';
  }, [startDate, currentStartDate, currentEndDate]);

  const resursDoubleBookingQueryOptions = useMemo<
    QueryHookOptions<
      GetResursDoubleBookingQuery,
      GetResursDoubleBookingQueryVariables
    >
  >(
    () => ({
      variables: {
        dbname: activeDatabase ?? '',
        routineID: routineID,
        franDatum: startDate,
        tillDatum: endDate,
      },
      skip:
        !activeDatabase ||
        !routineID ||
        startDate === '' ||
        endDate === '' ||
        startDate > endDate,
      fetchPolicy: 'network-only',
    }),
    [activeDatabase, startDate, routineID, endDate]
  );

  const { data: double_booking_data } = useQuery<
    GetResursDoubleBookingQuery,
    GetResursDoubleBookingQueryVariables
  >(resursDoubleBookingQuery, resursDoubleBookingQueryOptions);

  const currentDayBookings: ICurrentDayBooking[] = useMemo(
    () =>
      double_booking_data?.db?.BOBokningsrubrik_All?.map(item => ({
        aktId: item?._Associations?.Aktivitet?.id ?? '',
        BO_RESURS_ID: item?._Associations?.Resurs?.BO_RESURS_ID ?? '',
        resStart: combineDateAndTime(item?.FRANDATUM, item?.FRANTID),
        resEnd: combineDateAndTime(item?.TILLDATUM, item?.TILLTID),
        bokNr: item?._Associations?.Aktivitet?.BOKNINGSNUMMER ?? '',
        aktTitel: item?._Associations?.Aktivitet?.TITEL ?? '',
        varning_dubbelbokning:
          item?._Associations?.Resurs?.VARNING_DUBBELBOKNING,
      })) ?? [],
    [double_booking_data]
  );

  const xbridgeQueryOption = useMemo<
    QueryHookOptions<
      BOGetXBridgeBookingsQuery,
      BOGetXBridgeBookingsQueryVariables
    >
  >(
    () => ({
      variables: {
        dbname: activeDatabase ?? '',
        startdatum: startDate,
        slutdatum: endDate,
        resursId: resursLista,
      },
      skip:
        !activeDatabase ||
        !routineID ||
        startDate === '' ||
        endDate === '' ||
        resursLista.length === 0 ||
        startDate > endDate,
      fetchPolicy: 'network-only',
    }),
    [activeDatabase, startDate, resursLista, routineID, endDate]
  );

  const { data: double_booking_xbridge_data } = useQuery<
    BOGetXBridgeBookingsQuery,
    BOGetXBridgeBookingsQueryVariables
  >(xbridgeQuery, xbridgeQueryOption);

  const allDubbelBookings = useMemo<IOverlappedBooking[]>(() => {
    if (
      !formContext?.formState?.currentValues?._Associations?.AktBokRubrik ||
      !startDate ||
      !endDate
    ) {
      return [];
    }

    let newFormContext = clone(formContext);
    newFormContext.formState.currentValues.FRANDATUM = startDate;
    newFormContext.formState.currentValues.TILLDATUM = endDate;

    newFormContext.formState.currentValues._Associations.AktBokRubrik.forEach(
      (item: any) => {
        item.FRANDATUM = startDate;
        item.TILLDATUM = endDate;
      }
    );

    const currentDayXbridgeBookings = mapXbridgeBookings(
      double_booking_xbridge_data
    );

    const overLapped = overlappedBookings(
      currentDayBookings,
      currentDayXbridgeBookings,
      newFormContext?.formState?.currentValues
    );

    const isSameDateAsCurrent = startDate === currentStartDate;

    if (isSameDateAsCurrent) {
      const currentBookingResources = mapCurrentBookingToOverlappedBookings(
        formContext,
        currentStartDate,
        currentEndDate
      );
      overLapped.push(...currentBookingResources);
    }

    return overLapped || [];
  }, [
    startDate,
    endDate,
    formContext,
    currentDayBookings,
    double_booking_xbridge_data,
    currentStartDate,
    currentEndDate,
  ]);

  const closeDialog = (): void => {
    setOpen(false);
  };

  const isSaveDisabled = (): boolean => {
    return (
      isSaving ||
      isInvalidDate(startDate) ||
      !startDate ||
      startDate.trim() === '' ||
      isNaN(new Date(startDate).getTime()) ||
      !props.formContext?.formState?.currentValues?._Associations
        ?.AktBokRubrik?.[0]?.id
    );
  };

  const [kopieraMutation, { loading: isSaving }] = useMutation<
    KopieraAktivitetMutation,
    KopieraAktivitetMutationVariables
  >(kopieraAktivitetMutation, {
    refetchQueries: ['AktivitetQuery', 'rtnWebKalenderMain'],
  });

  const saveActivity = async (
    aktId: string,
    startDate: string
  ): Promise<void> => {
    if (!aktId || !startDate || !activeDatabase || isSaveDisabled()) {
      return;
    }
    const result = await kopieraMutation({
      variables: {
        dbname: activeDatabase ?? '',
        aktId: aktId,
        NyttFrandatum: startDate,
      },
    });
    const newActivityId = result.data?.rtn?.BOKopieraAktivitet?.ID;
    if (newActivityId) {
      props.onActivityCopied(newActivityId);
    }
    closeDialog();
  };

  return (
    <EDDialog
      title="Kopiera aktivitet"
      open={open}
      onClose={isSaving ? undefined : closeDialog}
      footerActions={[
        {
          label: 'Avbryt',
          testId: 'Kopiera aktivitet-Avbryt',
          onClick: closeDialog,
          buttonProps: {
            disabled: isSaving,
          },
        },
        {
          label: isSaving ? 'Sparar' : 'Spara',
          testId: 'Kopiera aktivitet-Spara',
          onClick: () => {
            saveActivity(
              props.formContext?.formState?.currentValues?.id,
              startDate
            );
          },
          buttonProps: {
            disabled: isSaveDisabled(),
          },
        },
      ]}
    >
      <ObjektVyKort
        width="100%"
        caption={isSaving ? '' : 'Välj datum för kopian'}
        noPadding
      >
        <EDBox sx={{ position: 'relative' }}>
          {isSaving && (
            <EDBox
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: themeColors.white[1000],
                opacity: 0.8,
                zIndex: 9999,
              }}
            >
              <EDAvenyProgress />
            </EDBox>
          )}
          <CopyActivityDatePicker
            startDate={startDate}
            setStartDate={setStartDate}
            isInvalidDate={isInvalidDate}
            isDateBehindCurrent={isDateBehindCurrent}
          />
          {startDate && (
            <EDBox marginBottom={'10px'}>
              <EDBox
                display="flex"
                alignItems="center"
                flexWrap={'wrap'}
                paddingLeft={'15px'}
              >
                <EDTypography
                  variant="overline"
                  color="textSecondary"
                  bold
                  data-testid="Nya datumet för kopian"
                  width={'100%'}
                  marginRight={'8px'}
                  marginBlock={'10px 5px'}
                  marginTop={'3px'}
                >
                  NYTT DATUM FÖR KOPIAN:
                </EDTypography>
              </EDBox>
              <EDBox
                display="flex"
                alignItems="center"
                width={'100%'}
                paddingLeft={'15px'}
              >
                <EDTypography marginRight="8px">{startDate}</EDTypography>
                {startDate !== endDate && (
                  <>
                    <EDTypography> - </EDTypography>
                    <EDTypography marginLeft="8px">{endDate}</EDTypography>
                  </>
                )}
              </EDBox>

              {allDubbelBookings.length > 0 && (
                <CopyActivityDoubleBooking
                  allDubbelBookings={allDubbelBookings}
                />
              )}
            </EDBox>
          )}
        </EDBox>
      </ObjektVyKort>
    </EDDialog>
  );
};

export default CopyActivityDialog;
